import React, { useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import NodeAutocomplete from "../../../NodeUI/NodeAutocomplete";
import NodeSection from "../../../NodeUI/NodeSection";
import { NODE_UI } from "../../../NodeUI/nodeUiStyles";
import AgentConfigSection from "../AgentConfigSection";
import SkillPickerCard from "../SkillPickerCard";
import { listSkills } from "../../../../utils/docsApi";

const HelperText = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  margin: 0,
}));

const EmptyState = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: NODE_UI.spacing.lg,
  padding: NODE_UI.spacing.lg,
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: NODE_UI.borderRadius,
  color: theme.palette.text.secondary,
  fontSize: NODE_UI.fontSize.value,
}));

const SkillGrid = styled("div")({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
  gap: NODE_UI.spacing.lg,
});

const LoadingWrap = styled("div")({
  display: "flex",
  justifyContent: "center",
  padding: `${NODE_UI.spacing.xl} 0`,
});

export default function SkillsTab({ enabledSkills, setEnabledSkills }) {
  const [availableSkills, setAvailableSkills] = useState([]);
  const [loadingSkills, setLoadingSkills] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoadingSkills(true);
    listSkills()
      .then((res) => {
        if (cancelled) return;
        setAvailableSkills(
          res.ok && Array.isArray(res.data) ? res.data : []
        );
      })
      .finally(() => !cancelled && setLoadingSkills(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const skillByName = useMemo(() => {
    const map = new Map();
    availableSkills.forEach((skill) => {
      const name = String(skill.skillName || "").toLowerCase();
      if (name) map.set(name, skill);
    });
    return map;
  }, [availableSkills]);

  const selectedSkillOptions = useMemo(() => {
    return (enabledSkills || []).map((value) => {
      const name = String(value).toLowerCase();
      const known = skillByName.get(name);
      return (
        known || {
          skillName: name,
          title: name,
          // Leave docId empty until listSkills resolves — skillName ≠ docId.
          docId: "",
        }
      );
    });
  }, [enabledSkills, skillByName]);

  const removeSkill = (skillName) => {
    const name = String(skillName).toLowerCase();
    setEnabledSkills(
      (enabledSkills || []).filter((s) => String(s).toLowerCase() !== name)
    );
  };

  return (
    <AgentConfigSection id="enabledSkills">
      <NodeSection title="Skills">
        <HelperText>
          Reusable playbooks from Docs. Add the ones this agent should know
          about — it can load the full instructions when a task matches.
        </HelperText>

        {loadingSkills ? (
          <LoadingWrap>
            <CircularProgress size={20} />
          </LoadingWrap>
        ) : availableSkills.length === 0 ? (
          <EmptyState>
            <AutoStoriesOutlinedIcon sx={{ fontSize: 18 }} />
            <span>
              No skills exist yet. Create a doc and promote it to a skill from
              the Docs page, then enable it here.
            </span>
          </EmptyState>
        ) : (
          <>
            <NodeAutocomplete
              label="Enabled skills"
              multiple
              inDialog
              options={availableSkills}
              value={selectedSkillOptions}
              loading={loadingSkills}
              placeholder="Add a skill..."
              noOptionsText="No matching skills"
              getOptionLabel={(option) =>
                option.skillName || option.title || ""
              }
              isOptionEqualToValue={(option, value) =>
                String(option.skillName || "").toLowerCase() ===
                String(value.skillName || "").toLowerCase()
              }
              onChange={(value) =>
                setEnabledSkills(
                  (value || [])
                    .map((skill) =>
                      String(skill.skillName || "").toLowerCase()
                    )
                    .filter(Boolean)
                )
              }
              renderOption={(props, option) => (
                <li {...props} key={option.docId || option.skillName}>
                  <Box>
                    <Typography
                      sx={{
                        fontSize: NODE_UI.fontSize.value,
                        fontWeight: 500,
                      }}
                    >
                      {option.skillName || option.title}
                    </Typography>
                    {option.title && option.title !== option.skillName ? (
                      <Typography
                        sx={{
                          fontSize: NODE_UI.fontSize.caption,
                          color: "text.secondary",
                        }}
                      >
                        {option.title}
                      </Typography>
                    ) : null}
                  </Box>
                </li>
              )}
            />

            {selectedSkillOptions.length > 0 ? (
              <SkillGrid>
                {selectedSkillOptions.map((skill) => (
                  <SkillPickerCard
                    key={skill.docId || skill.skillName}
                    skill={skill}
                    onRemove={removeSkill}
                  />
                ))}
              </SkillGrid>
            ) : (
              <HelperText>
                No skills enabled yet. Search above to add one.
              </HelperText>
            )}
          </>
        )}
      </NodeSection>
    </AgentConfigSection>
  );
}
